import { Badge } from '@capsule/consumer-design-system';

import { format } from 'date-fns';

import { Typography } from 'src/app/components/Elements/Typography';
import type { HomePrescriptionLifecycle, HomePrescriptionRefillStatus } from 'src/app/types';

import { PhoneNumberText, StatusText } from './styles';

interface StatusSectionProps {
  statusText: string;
  refillStatus?: HomePrescriptionRefillStatus;
  prescriptionLifecycle?: HomePrescriptionLifecycle;
  shouldShowNewBadge: boolean;
}

const formatRefillStatusText = (statusText: string) => {
  const regexMatch = statusText.match(/\+[/0-9]+/);
  if (!regexMatch) return false;
  // Formatting the phone number from '+19085668022' to '(908) 566-8022'
  const matchedPhone = regexMatch[0];
  const areaCode = matchedPhone.slice(2, 5);
  const prefix = matchedPhone.slice(5, 8);
  const lineNumber = matchedPhone.slice(8, matchedPhone.length);
  const formattedPhoneNumber = `(${areaCode}) ${prefix}-${lineNumber}`;
  // Need to split the original status text into groups so we can make the phone number callable using a tags
  const startIndexOfMatchedPhone = regexMatch.index!;
  const textBeforeMatch = statusText.slice(0, startIndexOfMatchedPhone);
  const textAfterMatch = statusText.slice(startIndexOfMatchedPhone + matchedPhone.length, statusText.length);
  return { textBeforeMatch, textAfterMatch, originalPhoneNumber: matchedPhone, formattedPhoneNumber };
};

export const StatusTextSection = ({
  statusText,
  prescriptionLifecycle,
  refillStatus,
  shouldShowNewBadge,
}: StatusSectionProps) => {
  if (refillStatus) {
    if (shouldShowNewBadge) {
      return <Badge>New</Badge>;
    }

    return (
      <>
        {prescriptionLifecycle && (
          <Typography variant="overline" marginBottom={'s2'}>
            {format(new Date(prescriptionLifecycle.phaseDate), 'M/d/yy h:mma')}
          </Typography>
        )}
        <Typography variant="small">{refillStatus?.status}</Typography>
      </>
    );
  }

  const formattedExternalStatus = statusText && formatRefillStatusText(statusText);
  // We only show below when there there is a phone number in the refill status text.
  if (formattedExternalStatus) {
    const { textBeforeMatch, textAfterMatch, formattedPhoneNumber, originalPhoneNumber } = formattedExternalStatus;
    return (
      <StatusText>
        {textBeforeMatch}
        <a href={`tel:${originalPhoneNumber}`}>
          <PhoneNumberText>{formattedPhoneNumber}</PhoneNumberText>
        </a>
        {textAfterMatch}
      </StatusText>
    );
  }
  return <small>{statusText}</small>;
};
